import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Download, KeyRound, Users, RefreshCw, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import RouteSheet from "@/components/admin/RouteSheet";

export default function AdminPage() {
  const [key, setKey] = useState(sessionStorage.getItem("dd_admin_key") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers", { headers: { "X-Admin-Key": k } });
      setData(data);
      sessionStorage.setItem("dd_admin_key", k);
    } catch (e) {
      toast.error("Invalid admin key.");
      setData(null);
    }
    setLoading(false);
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/subscribers/export`, { headers: { "X-Admin-Key": key } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <main className="min-h-screen bg-charcoal text-parchment px-6 py-24 grain relative" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center">
            <Building2 className="w-4 h-4 text-charcoal" strokeWidth={2.4} />
          </span>
          <h1 className="font-display font-bold text-2xl sm:text-3xl">Subscriber Dashboard</h1>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-dim mb-10">Doorstep Disposal Co. · Admin</p>

        {!data ? (
          <motion.form
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            onSubmit={(e) => { e.preventDefault(); load(key); }}
            className="glass rounded-3xl p-8 max-w-md"
            data-testid="admin-login-form"
          >
            <label className="text-xs text-mist flex items-center gap-2 mb-3">
              <KeyRound className="w-3.5 h-3.5" /> Admin key
            </label>
            <div className="flex gap-3">
              <Input type="password" value={key} onChange={(e) => setKey(e.target.value)}
                data-testid="admin-key-input" placeholder="Enter admin key"
                className="bg-white/5 border-white/10 text-parchment placeholder:text-dim" />
              <button type="submit" data-testid="admin-login-button" disabled={loading}
                className="shrink-0 bg-terracotta hover:bg-parchment hover:text-charcoal text-parchment text-sm font-semibold px-6 rounded-full transition-colors duration-300 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "View"}
              </button>
            </div>
          </motion.form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
              {[
                { label: "Total", value: data.stats.total },
                { label: "Basic", value: data.stats.basic },
                { label: "Standard", value: data.stats.standard },
                { label: "On-Demand", value: data.stats.ondemand },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                  <p className="font-display font-extrabold text-3xl">{s.value}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-4">
              <p className="flex items-center gap-2 text-sm text-mist"><Users className="w-4 h-4" /> {data.stats.total} subscribers</p>
              <div className="flex gap-3">
                <button onClick={() => load(key)} data-testid="admin-refresh-button"
                  className="flex items-center gap-2 text-xs border border-white/15 hover:border-terracotta px-4 py-2 rounded-full transition-colors">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                <RouteSheet adminKey={key} />
                <button onClick={exportCsv} data-testid="admin-export-button"
                  className="flex items-center gap-2 text-xs bg-terracotta hover:bg-parchment hover:text-charcoal px-4 py-2 rounded-full font-semibold transition-colors">
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 overflow-hidden overflow-x-auto" data-testid="admin-subscriber-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.04] text-left font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                    <th className="px-5 py-3.5">Resident</th>
                    <th className="px-5 py-3.5">Unit</th>
                    <th className="px-5 py-3.5">Plan</th>
                    <th className="px-5 py-3.5">Window</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscribers.length === 0 && (
                    <tr><td colSpan="6" className="px-5 py-10 text-center text-dim" data-testid="admin-empty-state">No subscribers yet — share the link with residents.</td></tr>
                  )}
                  {data.subscribers.map((s) => (
                    <tr key={s.subscriber_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${s.unit_number}`}>
                      <td className="px-5 py-4">
                        <p className="font-medium">{s.name}</p>
                        <p className="text-xs text-dim">{s.email}</p>
                      </td>
                      <td className="px-5 py-4 font-mono text-terracotta">{s.unit_number}</td>
                      <td className="px-5 py-4">{s.plan_name}{s.plan_lookup_key === "ondemand_bag" ? ` ×${s.quantity}` : ""}</td>
                      <td className="px-5 py-4 text-mist">{s.pickup_window}</td>
                      <td className="px-5 py-4">
                        <span className="text-[10px] font-mono uppercase tracking-widest bg-forest/60 text-parchment px-2.5 py-1 rounded-full">{s.status}</span>
                      </td>
                      <td className="px-5 py-4 text-dim text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
